/**
 * Thermal printer model presets (USB serial baud + BLE write tuning).
 * Values are hardcoded for Zeettu-supported Xprinter references; users may override per browser on the help page.
 */

import { getXpP203aBleLinkDefaults } from './xpP203aBleSettings';

const LS_MODEL_ID = 'zeettu.printerModel.id';
const LS_LINK_OVERRIDES = 'zeettu.printerModel.linkOverrides';

export type PrinterModelId = 'xp-p203a' | 'xp-p801a' | 'xp-p810';

export type PrinterLinkOverrides = {
  serialBaud?: number;
  /** Display / documentation; chunk size is explicit on preset — override if needed */
  bleUartBaudHint?: number;
  bleChunkBytes?: number;
  bleInterChunkDelayMs?: number;
  bleWriteWithResponse?: boolean;
  /** After last chunk of a print; 0–500 ms */
  blePostWriteSettleMs?: number;
};

export type PrinterModelPreset = {
  id: PrinterModelId;
  /** Public asset under / */
  imageSrc: string;
  /** Web Serial `port.open({ baudRate })` */
  serialBaud: number;
  /** Typical internal BLE-UART speed (informational; tuning uses chunk/delay/response) */
  bleUartBaudHint: number;
  bleChunkBytes: number;
  bleInterChunkDelayMs: number;
  bleWriteWithResponse: boolean;
  /**
   * After the last GATT write of a print job, wait this long so bytes drain through the
   * BLE–UART bridge before the UI leaves “printing” (0 = none).
   */
  blePostWriteSettleMs: number;
};

export const PRINTER_MODEL_PRESETS: Record<PrinterModelId, PrinterModelPreset> = {
  // XP-P203A (BLE only): field units often ACK writeValue (with response) in JS while the
  // internal UART never prints; writeWithoutResponse + very small ATT payloads + long
  // pacing matches many Xprinter BLE bridges better. WebBluetoothTransport also prepends a
  // short null wake, caps WNR size further for this model, and avoids picking NUS TX as write.
  'xp-p203a': {
    id: 'xp-p203a',
    imageSrc: '/XP-P203A.jpeg',
    /** LABEL mode + RFCOMM: self-test reports Bluetooth 460800. */
    serialBaud: 460800,
    bleUartBaudHint: 460800,
    bleChunkBytes: 14,
    bleInterChunkDelayMs: 55,
    bleWriteWithResponse: false,
    blePostWriteSettleMs: 280,
  },
  'xp-p801a': {
    id: 'xp-p801a',
    imageSrc: '/XP-P801A.jpeg',
    serialBaud: 9600,
    bleUartBaudHint: 115200,
    bleChunkBytes: 128,
    bleInterChunkDelayMs: 0,
    bleWriteWithResponse: false,
    blePostWriteSettleMs: 0,
  },
  'xp-p810': {
    id: 'xp-p810',
    imageSrc: '/XP-P810.jpeg',
    serialBaud: 9600,
    bleUartBaudHint: 115200,
    bleChunkBytes: 128,
    bleInterChunkDelayMs: 0,
    bleWriteWithResponse: false,
    blePostWriteSettleMs: 0,
  },
};

export const PRINTER_MODEL_ORDER: PrinterModelId[] = ['xp-p203a', 'xp-p801a', 'xp-p810'];

const DEFAULT_MODEL_ID: PrinterModelId = 'xp-p203a';

function isPrinterModelId(s: string): s is PrinterModelId {
  return s === 'xp-p203a' || s === 'xp-p801a' || s === 'xp-p810';
}

export function getStoredPrinterModelId(): PrinterModelId {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) {
    return DEFAULT_MODEL_ID;
  }
  try {
    const raw = globalThis.localStorage.getItem(LS_MODEL_ID)?.trim() ?? '';
    if (isPrinterModelId(raw)) return raw;
  } catch {
    /* ignore */
  }
  return DEFAULT_MODEL_ID;
}

export function setStoredPrinterModelId(id: PrinterModelId): void {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) return;
  try {
    globalThis.localStorage.setItem(LS_MODEL_ID, id);
  } catch {
    /* quota */
  }
}

function readStoredOverridesRaw(): PrinterLinkOverrides {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) {
    return {};
  }
  try {
    const raw = globalThis.localStorage.getItem(LS_LINK_OVERRIDES)?.trim();
    if (!raw) return {};
    const j = JSON.parse(raw) as Record<string, unknown>;
    const o: PrinterLinkOverrides = {};
    if (typeof j.serialBaud === 'number' && Number.isFinite(j.serialBaud) && j.serialBaud > 0) {
      o.serialBaud = Math.floor(j.serialBaud);
    }
    if (typeof j.bleUartBaudHint === 'number' && Number.isFinite(j.bleUartBaudHint) && j.bleUartBaudHint > 0) {
      o.bleUartBaudHint = Math.floor(j.bleUartBaudHint);
    }
    if (typeof j.bleChunkBytes === 'number' && Number.isFinite(j.bleChunkBytes) && j.bleChunkBytes > 0) {
      o.bleChunkBytes = Math.floor(j.bleChunkBytes);
    }
    if (typeof j.bleInterChunkDelayMs === 'number' && Number.isFinite(j.bleInterChunkDelayMs) && j.bleInterChunkDelayMs >= 0) {
      o.bleInterChunkDelayMs = Math.floor(j.bleInterChunkDelayMs);
    }
    if (typeof j.bleWriteWithResponse === 'boolean') {
      o.bleWriteWithResponse = j.bleWriteWithResponse;
    }
    if (
      typeof j.blePostWriteSettleMs === 'number' &&
      Number.isFinite(j.blePostWriteSettleMs) &&
      j.blePostWriteSettleMs >= 0
    ) {
      o.blePostWriteSettleMs = Math.floor(j.blePostWriteSettleMs);
    }
    return o;
  } catch {
    return {};
  }
}

export function getStoredPrinterLinkOverrides(): PrinterLinkOverrides {
  return readStoredOverridesRaw();
}

export function savePrinterLinkOverrides(overrides: PrinterLinkOverrides | null): void {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) return;
  try {
    if (!overrides || Object.keys(overrides).length === 0) {
      globalThis.localStorage.removeItem(LS_LINK_OVERRIDES);
      return;
    }
    globalThis.localStorage.setItem(LS_LINK_OVERRIDES, JSON.stringify(overrides));
  } catch {
    /* quota */
  }
}

export function clearPrinterLinkOverrides(): void {
  savePrinterLinkOverrides(null);
}

/** Portable BLE bridges (XP-P203A) need 14-byte ATT payloads; do not clamp below this. */
const BLE_CHUNK_MIN = 14;
const BLE_CHUNK_MAX = 244;

function clampChunk(n: number): number {
  return Math.min(BLE_CHUNK_MAX, Math.max(BLE_CHUNK_MIN, n));
}

/** Effective link settings for transports (preset + per-device overrides). */
export function getEffectivePrinterLinkSettings(): {
  modelId: PrinterModelId;
  preset: PrinterModelPreset;
  serialBaud: number;
  bleChunkBytes: number;
  bleInterChunkDelayMs: number;
  bleWriteWithResponse: boolean;
  bleUartBaudHint: number;
  blePostWriteSettleMs: number;
} {
  const modelId = getStoredPrinterModelId();
  const preset = PRINTER_MODEL_PRESETS[modelId];
  const ov = readStoredOverridesRaw();
  const serialBaud = ov.serialBaud ?? preset.serialBaud;
  const bleUartBaudHint = ov.bleUartBaudHint ?? preset.bleUartBaudHint;

  // XP-P203A BLE: device-saved tuning (xpP203aBleSettings) + serial baud overrides.
  if (modelId === 'xp-p203a') {
    const ble = getXpP203aBleLinkDefaults();
    return {
      modelId,
      preset,
      serialBaud,
      bleChunkBytes: ble.bleChunkBytes,
      bleInterChunkDelayMs: ble.bleInterChunkDelayMs,
      bleWriteWithResponse: ble.bleWriteWithResponse,
      bleUartBaudHint: preset.bleUartBaudHint,
      blePostWriteSettleMs: ble.blePostWriteSettleMs,
    };
  }

  const bleChunkBytes = clampChunk(ov.bleChunkBytes ?? preset.bleChunkBytes);
  const bleInterChunkDelayMs = Math.min(
    100,
    Math.max(0, ov.bleInterChunkDelayMs ?? preset.bleInterChunkDelayMs),
  );
  const bleWriteWithResponse = ov.bleWriteWithResponse ?? preset.bleWriteWithResponse;
  const blePostWriteSettleMs = Math.min(
    500,
    Math.max(0, ov.blePostWriteSettleMs ?? preset.blePostWriteSettleMs),
  );
  return {
    modelId,
    preset,
    serialBaud,
    bleChunkBytes: clampChunk(bleChunkBytes),
    bleInterChunkDelayMs,
    bleWriteWithResponse,
    bleUartBaudHint,
    blePostWriteSettleMs,
  };
}
